import type { MemoryEntry } from './ws-protocol.ts';

export const MAX_VISITOR_FACTS = 15;
export const MAX_VISITOR_FACT_LENGTH = 500;

export type VisitorContextUpdate =
  | { operation: 'upsert'; summary: string; id?: string }
  | { operation: 'remove'; id: string }
  | { operation: 'clear' };

/** Shared browser/voice boundary. Legacy summary-only updates remain additions. */
export function readVisitorContextUpdate(value: unknown): VisitorContextUpdate | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const operation = 'operation' in value ? value.operation : 'upsert';
  if (operation === 'clear') {
    return { operation };
  }
  const id = 'id' in value ? value.id : undefined;
  if (id !== undefined && (typeof id !== 'string' || !id.trim() || id.length > 200)) {
    return null;
  }
  if (operation === 'remove') {
    return typeof id === 'string' ? { operation, id } : null;
  }
  if (operation !== 'upsert' || !('summary' in value) || typeof value.summary !== 'string') {
    return null;
  }
  const summary = value.summary.trim();
  if (!summary || summary.length > MAX_VISITOR_FACT_LENGTH) {
    return null;
  }
  return { operation, summary, ...(typeof id === 'string' ? { id } : {}) };
}

export function isVisitorFact(value: unknown): value is MemoryEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    value.id.length > 0 &&
    value.id.length <= 200 &&
    'summary' in value &&
    typeof value.summary === 'string' &&
    value.summary.trim().length > 0 &&
    value.summary.length <= MAX_VISITOR_FACT_LENGTH &&
    'timestamp' in value &&
    typeof value.timestamp === 'number' &&
    Number.isFinite(value.timestamp)
  );
}
